import Dexie, { type EntityTable } from 'dexie';
import type {
  SyncEntityType,
  SyncPayload,
  CollaborationAccess,
} from '../types/sync';

export interface LocalRecord {
  key: string;
  userId: string;
  entityType: SyncEntityType;
  entityId: string;
  schemaVersion: number;
  version: number;
  cursor: string;
  payload: SyncPayload;
  serverPayload?: SyncPayload;
  dirty: 0 | 1;
  deleted: 0 | 1;
  updatedAt: string;
  access?: CollaborationAccess;
}

export interface OutboxEntry {
  key: string;
  userId: string;
  mutationId: string;
  entityType: SyncEntityType;
  entityId: string;
  operation: 'upsert' | 'delete';
  baseVersion: number;
  schemaVersion: number;
  payload?: SyncPayload;
  basePayload?: SyncPayload;
  createdAt: string;
}

export interface ConflictEntry {
  key: string;
  userId: string;
  entityType: SyncEntityType;
  entityId: string;
  reason: string;
  conflictingPaths: string[];
  basePayload?: SyncPayload;
  localPayload: SyncPayload;
  remotePayload?: SyncPayload;
  remoteVersion: number;
  detectedAt: string;
}

export interface MetaEntry {
  key: string;
  value: string;
}

export interface LocalAssetEntry {
  key: string;
  userId: string;
  assetId: string;
  entityType: SyncEntityType;
  entityId: string;
  /** 稳定的文档内资源槽位；同一槽位可合并尚未上传的新版二进制。 */
  descriptorId?: string;
  kind: 'image' | 'drawing' | 'richTextRTF';
  mediaType: string;
  byteCount: number;
  checksumSha256: string;
  data: Blob;
  status: 'pending' | 'uploading' | 'ready' | 'failed';
  retryCount: number;
  nextAttemptAt?: string;
  lastError?: string;
  updatedAt: string;
}

class ClassMindDatabase extends Dexie {
  records!: EntityTable<LocalRecord, 'key'>;
  outbox!: EntityTable<OutboxEntry, 'key'>;
  conflicts!: EntityTable<ConflictEntry, 'key'>;
  meta!: EntityTable<MetaEntry, 'key'>;
  assets!: EntityTable<LocalAssetEntry, 'key'>;

  constructor() {
    super('classmind-web-v1');
    this.version(1).stores({
      records: '&key, userId, [userId+entityType], [userId+dirty], updatedAt',
      outbox: '&key, &mutationId, userId, createdAt',
      conflicts: '&key, userId, detectedAt',
      meta: '&key',
    });
    this.version(2).stores({
      records: '&key, userId, [userId+entityType], [userId+dirty], updatedAt',
      outbox: '&key, &mutationId, userId, createdAt',
      conflicts: '&key, userId, detectedAt',
      meta: '&key',
      assets: '&key, userId, [userId+assetId], [userId+status], [userId+entityType+entityId], updatedAt',
    });
    this.version(3).stores({
      records: '&key, userId, [userId+entityType], [userId+dirty], updatedAt',
      outbox: '&key, &mutationId, userId, createdAt',
      conflicts: '&key, userId, detectedAt',
      meta: '&key',
      assets: '&key, userId, [userId+assetId], [userId+descriptorId], [userId+status], [userId+entityType+entityId], updatedAt',
    });
  }
}

export const database = new ClassMindDatabase();

export function recordKey(userId: string, entityType: SyncEntityType, entityId: string) {
  return `${userId}:${entityType}:${entityId}`;
}

export function userMetaKey(userId: string, name: string) {
  return `${userId}:${name}`;
}

export function assetKey(userId: string, assetId: string) {
  return `${userId}:${assetId}`;
}
